import { useEffect, useRef } from 'react';

import type { AnalyticsKeyValue } from '$src/utils/analytics/types';

import { type AnalyticsEventName, useTrackAnalytics } from '../useTrackAnalytics';

/**
 * @description Tracks analytics events based on boolean transitions of a value.
 *
 * @example
 * useTrackTransition({
 *   value: true,
 *   trueEventName: 'TRUTHY_EVENT_NAME',
 *   falseEventName: 'FALSY_EVENT_NAME',
 *   skipInitial: true, // Don't track on mount
 * });
 *
 */
export function useTrackTransition<Flag = unknown>({
  value,
  trueEventName,
  falseEventName,
  skipInitial = false,
  payload,
}: {
  value: Flag;
  trueEventName?: AnalyticsEventName;
  falseEventName?: AnalyticsEventName;
  skipInitial?: boolean;
  payload?: AnalyticsKeyValue;
}) {
  const { track } = useTrackAnalytics();
  const prevValueRef = useRef<Flag | undefined>(undefined);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const isTruthy = Boolean(value);
    const hasChanged = prevValueRef.current !== value;

    // first render will evaluate => !skipInitial
    // subsequent renders will evaluate => hasChanged
    const shouldTrack = isFirstRender.current ? !skipInitial : hasChanged;

    if (shouldTrack) {
      const eventName = isTruthy ? trueEventName : falseEventName;
      if (eventName) track(eventName, payload);
    }

    prevValueRef.current = value;
    isFirstRender.current = false;
  }, [trueEventName, falseEventName, skipInitial, track, value, payload]);
}
